import { useUsers } from "@/lib/api/user.ts";
import { cn } from "@/lib/utils.ts";
import { Check, ChevronsUpDown } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.tsx";
import {
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  Command,
  CommandList,
} from "@/components/ui/command.tsx";
import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button.tsx";
import { CommandLoading } from "cmdk";

interface MentorsSelectProps {
  mentorId: string | null;
  handleFilterChange: (name: string, value: string, label?: string) => void;
  isMulti?: boolean;
  selectedMentors?: Array<{ id: string; name: string }>;
  assignedMentors?: Array<{ id: string; name: string }>;
}

const encodeValue = (value: string, keywords: string[] = []) =>
  `${keywords.join("|")}|${value}`;
const decodeValue = (value: string) => value.split("|").at(-1) as string;

export const mergeUsersById = <T extends { id: string }>(
  ...lists: Array<T[] | undefined>
): T[] => {
  const seen = new Set<string>();
  return lists.flatMap((list) => list ?? []).filter((user) => {
    if (seen.has(user.id)) return false;
    seen.add(user.id);
    return true;
  });
};

export function MentorsSelect({
  mentorId,
  handleFilterChange,
  isMulti = false,
  selectedMentors = [],
  assignedMentors = [],
}: MentorsSelectProps) {
  const [open, setOpen] = useState(false);
  const mentorsQuery = useUsers({
    page: 1,
    perPage: 1000,
    assignedRole: "mentor",
    status: "active",
  });
  const leadMentorsQuery = useUsers({
    page: 1,
    perPage: 1000,
    assignedRole: "lead_mentor",
    status: "active",
  });

  const mentors = useMemo(
    () =>
      mergeUsersById(mentorsQuery.data?.users, leadMentorsQuery.data?.users),
    [leadMentorsQuery.data?.users, mentorsQuery.data?.users],
  );
  const isPending = mentorsQuery.isPending || leadMentorsQuery.isPending;

  const availableMentors = mentors.filter(
    (mentor) => !assignedMentors.some((assigned) => assigned.id === mentor.id),
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between"
        >
          {isMulti
            ? selectedMentors.length > 0
              ? `${selectedMentors.length} mentor${selectedMentors.length > 1 ? "s" : ""} selected`
              : "Select mentors..."
            : mentorId
              ? mentors.find((mentor) => mentor.id === mentorId)?.name
              : "Select mentor..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command>
          <CommandInput placeholder="Search mentors..." />

          <CommandList>
            <CommandEmpty>No mentors found.</CommandEmpty>
            {isPending && <CommandLoading>Hang on…</CommandLoading>}

            <CommandGroup>
              {!isMulti && (
                <CommandItem
                  value="all"
                  keywords={["all"]}
                  onSelect={() => {
                    handleFilterChange("mentorId", "", "All");
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      mentorId === "" ? "opacity-100" : "opacity-0",
                    )}
                  />
                  All
                </CommandItem>
              )}
              {availableMentors?.map((mentor) => (
                <CommandItem
                  key={mentor.id}
                  value={encodeValue(mentor.id, [mentor.name ?? ""])}
                  keywords={[mentor.name ?? ""]}
                  onSelect={(currentValue) => {
                    const decodedValue = decodeValue(currentValue);
                    if (!isMulti) {
                      if (currentValue === mentorId) {
                        handleFilterChange("mentorId", "all", "All");
                      } else {
                        handleFilterChange(
                          "mentorId",
                          decodedValue,
                          mentor.name ?? undefined,
                        );
                      }
                      setOpen(false);
                    } else {
                      handleFilterChange(
                        "mentorId",
                        decodedValue,
                        mentor.name ?? undefined,
                      );
                      // Don't close in multi-select mode
                    }
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      isMulti
                        ? selectedMentors.some((m) => m.id === mentor.id)
                          ? "opacity-100"
                          : "opacity-0"
                        : mentorId === mentor.id
                          ? "opacity-100"
                          : "opacity-0",
                    )}
                  />
                  {mentor.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
